import axiosRetry from 'axios-retry';
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import Agent, { HttpsAgent } from 'agentkeepalive';
import authService from '../auth/services/auth.service';

const agentKeepAliveSettings = {
  keepAlive: true,
  maxSockets: 128,
  maxFreeSockets: 128,
  timeout: 60000,
  freeSocketTimeout: 30000,
};

axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
});

export class RestClient {
  private readonly _client: AxiosInstance;

  private constructor(public config: AxiosRequestConfig) {
    this._client = axios.create({
      ...config,
      httpAgent: new Agent(agentKeepAliveSettings),
      httpsAgent: new HttpsAgent(agentKeepAliveSettings),
    });
    this.initRequestInterceptor();
    this.initResponseInterceptor();
  }

  private initRequestInterceptor(): void {
    // includes token in the Authorization header
    this._client.interceptors.request.use(
      async (config) => {
        let token: string | undefined = authService.getToken();
        if (!token) {
          token = await authService.refreshToken();
        }
        config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['Ocp-Apim-Subscription-Key'] =
          process.env.REACT_APP_SUBSCRIPTION_KEY;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  private initResponseInterceptor(): void {
    this._client.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;

        // if we already retried the request, and we have a different error then 401
        if (originalRequest._retry || error.response.status !== 401) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        try {
          // Dispatch event indicating that the token refresh is starting
          window.dispatchEvent(new Event('token-refresh-start'));

          // Refresh the token
          const newToken: string = await authService.refreshToken();
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          // Dispatch event indicating that the token refresh is complete
          window.dispatchEvent(new Event('token-refresh-end'));

          // Retry the original request with the new token
          return this._client(originalRequest);
        } catch (error) {
          // Dispatch event indicating that the token refresh failed
          window.dispatchEvent(new Event('token-refresh-end'));
          return Promise.reject(error);
        }
      }
    );
  }

  private static _instance: RestClient;

  public static getInstance(config: AxiosRequestConfig = {}): RestClient {
    if (!RestClient._instance) {
      RestClient._instance = new RestClient(config);
    }
    return RestClient._instance;
  }

  public get = <T = any>(
    url: string,
    options: AxiosRequestConfig = {}
  ): Promise<AxiosResponse<T>> => {
    return this._client.get<T>(url, options);
  };

  public put = <T = any>(
    url: string,
    body: any,
    options: AxiosRequestConfig = {}
  ) => {
    return this._client.put<T>(url, body, options);
  };

  public patch = <T = any>(
    url: string,
    body: any,
    options: AxiosRequestConfig = {}
  ) => {
    return this._client.patch<T>(url, body, options);
  };

  public post = <T = any>(
    url: string,
    body: any,
    options: AxiosRequestConfig = {}
  ) => {
    return this._client.post<T>(url, body, options);
  };

  public delete = <T = any>(url: string, options: AxiosRequestConfig = {}) => {
    return this._client.delete<T>(url, options);
  };
}
